import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BlogLayout from "./BlogLayout";
import {
  CheckCircle2,
  FileCheck,
  CreditCard,
  Package,
  Calendar,
  Settings,
  Globe,
  Megaphone,
  ArrowRight,
  ExternalLink,
} from "lucide-react";

const HowToStartPage: React.FC = () => {
  const steps = [
    {
      number: "01",
      title: "必要なものを準備する",
      icon: <FileCheck className="w-8 h-8" />,
      description: "オリパサイト運営に必要な書類・アカウントを用意します",
      items: [
        {
          label: "古物商許可証",
          description:
            "トレーディングカードなどを取り扱うために必要です。お住まいの都道府県の公安委員会で申請できます。",
        },
        { label: "銀行口座", description: "売上の入金先として必要です。" },
        {
          label: "販売する商品",
          description: "オリパで提供する商品（カードなど）を準備しましょう。",
        },
      ],
    },
    {
      number: "02",
      title: "アカウントを作成する",
      icon: <CheckCircle2 className="w-8 h-8" />,
      description: "JoyBaseに登録してダッシュボードにアクセス",
      items: [
        {
          label: "会員登録",
          description:
            "基本情報を入力し、利用規約とプライバシーポリシーに同意して登録を完了させます。",
          link: { text: "会員登録ページ", url: "https://dashboard.joybase.jp/signup" },
        },
      ],
    },
    {
      number: "03",
      title: "オリパサイトを開設する",
      icon: <Globe className="w-8 h-8" />,
      description: "あなた専用のオリパサイトを作成",
      items: [
        { label: "サイト名", description: "オリパサイトの名前を設定" },
        {
          label: "サブドメイン",
          description: "〇〇〇.joybase.jp の「〇〇〇」部分を決定",
        },
        { label: "サイトの説明", description: "サイトの概要や特徴を記載" },
      ],
    },
    {
      number: "04",
      title: "商品を登録する",
      icon: <Package className="w-8 h-8" />,
      description: "販売する商品（カード）の情報を登録",
      items: [
        { label: "商品名", description: "わかりやすい商品名を設定" },
        {
          label: "商品説明",
          description: "商品の詳細や含まれるカードの情報を記載",
        },
        { label: "商品画像", description: "魅力的な画像をアップロード" },
      ],
    },
    {
      number: "05",
      title: "イベントを作成する",
      icon: <Calendar className="w-8 h-8" />,
      description: "オリパの販売イベントを設定",
      items: [
        { label: "イベント名", description: "わかりやすいイベント名を設定" },
        { label: "開催期間", description: "イベントの開始・終了日時を設定" },
        { label: "カテゴリ", description: "イベントのカテゴリを選択（任意）" },
      ],
    },
    {
      number: "06",
      title: "在庫と賞品を設定する",
      icon: <Settings className="w-8 h-8" />,
      description: "商品の在庫数と当選確率を調整",
      items: [
        { label: "商品の割り当て", description: "イベントに提供する商品を選択" },
        { label: "在庫数", description: "用意する商品の数量を設定" },
        { label: "当選確率", description: "商品ごとの当選確率を調整" },
      ],
    },
    {
      number: "07",
      title: "決済設定をする",
      icon: <CreditCard className="w-8 h-8" />,
      description: "銀行口座情報を登録して決済を有効化",
      items: [
        {
          label: "銀行口座登録",
          description: "売上の入金先となる銀行口座情報を登録します",
        },
        {
          label: "本人確認",
          description: "本人確認書類を提出して審査を完了させます",
        },
        {
          label: "決済テスト",
          description: "決済が正常に機能することを確認しましょう",
        },
      ],
    },
    {
      number: "08",
      title: "サイトを公開する",
      icon: <Globe className="w-8 h-8" />,
      description: "すべての設定を確認して公開",
      items: [
        { label: "内容確認", description: "サイトの内容や設定を最終チェック" },
        {
          label: "テスト購入",
          description: "実際に購入フローを確認することを推奨します",
        },
        {
          label: "公開",
          description: "「公開する」を選択してオリパサイトを公開",
        },
      ],
    },
    {
      number: "09",
      title: "宣伝・集客する",
      icon: <Megaphone className="w-8 h-8" />,
      description: "SNSやブログで集客を開始",
      items: [
        {
          label: "SNS活用",
          description: "Twitter、Instagram、YouTubeなどで宣伝",
        },
        {
          label: "魅力的な特典",
          description: "キャンペーンや特典を用意して集客効果を高める",
        },
      ],
    },
  ];

  const checklist = [
    "古物商許可証を取得済み",
    "銀行口座を準備済み",
    "販売する商品を準備済み",
    "JoyBaseアカウントを作成済み",
    "サイト名とサブドメインを決定済み",
  ];

  return (
    <BlogLayout
      title="オリパサイトの始め方"
      description="JoyBaseを使えば簡単にオリパサイトを開設できます。サイト開設から運営までの流れをわかりやすく解説します。"
      category="始め方"
      publishedAt="2025年12月1日"
      breadcrumbs={[{ label: "オリパサイトの始め方" }]}
    >
      <Helmet>
        <title>【2025年最新】オリパサイトの始め方完全ガイド | JoyBase</title>
        <meta
          name="description"
          content="JoyBaseでオリパサイトを始める方法をご紹介します。古物商許可証と銀行口座の準備から、サイト開設、商品登録、ガチャ設定まで、わかりやすく解説します。"
        />
        <meta
          name="keywords"
          content="オリパサイトの始め方,JoyBase,オリパサイト開設,オリパ運営,トレーディングカード"
        />
        <meta
          property="og:title"
          content="【2025年最新】オリパサイトの始め方完全ガイド | JoyBase"
        />
        <meta
          property="og:description"
          content="JoyBaseでオリパサイトを始める方法をご紹介します。古物商許可証と銀行口座の準備から、サイト開設、商品登録、ガチャ設定まで。"
        />
        <meta property="og:url" content="https://joybase.jp/blog/how-to-start" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://joybase.jp/blog/how-to-start" />
      </Helmet>

      {/* ステップセクション */}
      <section className="mb-12">
        <h2>開設までの9ステップ</h2>
        <p>
          JoyBaseでオリパサイトを開設するまでの流れを、9つのステップでわかりやすく解説します。
        </p>

        <div className="space-y-8 not-prose mt-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                {/* ステップ番号とアイコン */}
                <div className="flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-yellow-400 text-black font-bold text-lg rounded-full">
                      {step.number}
                    </div>
                    <div className="flex items-center justify-center w-12 h-12 bg-yellow-50 text-yellow-600 rounded-full">
                      {step.icon}
                    </div>
                  </div>
                </div>

                {/* コンテンツ */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{step.description}</p>

                  <div className="space-y-3">
                    {step.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <CheckCircle2 className="w-5 h-5 text-yellow-500" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">
                            {item.label}
                          </span>
                          <p className="text-sm text-gray-600 mt-1">
                            {item.description}
                            {item.link && (
                              <>
                                {" "}
                                <a
                                  href={item.link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                                >
                                  {item.link.text}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* チェックリストセクション */}
      <section className="mb-12">
        <h2>始めるためのチェックリスト</h2>
        <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl p-6 not-prose">
          <div className="space-y-3">
            {checklist.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 bg-white bg-opacity-20 rounded-lg p-3"
              >
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 rounded-full border-2 border-black"></div>
                </div>
                <span className="text-black font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 関連リンク */}
      <section>
        <h2>関連記事</h2>
        <div className="not-prose">
          <Link
            to="/blog/kobutsusho-guide"
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowRight className="w-4 h-4" />
            古物商許可証の取り方完全ガイド
          </Link>
        </div>
      </section>
    </BlogLayout>
  );
};

export default HowToStartPage;
